import { useEffect, useMemo, useState } from 'react'
import { Camera, ImagePlus, Plus, UserRound } from 'lucide-react'
import { api, ApiError } from '../api'
import { Button, Field, Modal, SelectField, Status } from '../components'
import type { DriverUser } from '../types'

const initialForm = { fullName: '', email: '', phone: '', role: 'DRIVER', password: '', preferredNavigation: 'google' }

export function TeamPage() {
  const [users, setUsers] = useState<DriverUser[]>([])
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [photo, setPhoto] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [uploadingId, setUploadingId] = useState('')
  const [error, setError] = useState('')
  const previewUrl = useMemo(() => photo ? URL.createObjectURL(photo) : '', [photo])

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])
  const load = async () => setUsers((await api<{ users: DriverUser[] }>('/api/users')).users)
  useEffect(() => { void load() }, [])

  const closeCreator = () => {
    setOpen(false)
    setForm(initialForm)
    setPhoto(null)
    setError('')
  }

  const create = async () => {
    setBusy(true)
    setError('')
    try {
      const payload = new FormData()
      Object.entries(form).forEach(([key, value]) => payload.set(key, value))
      if (photo) payload.set('photo', photo)
      await api('/api/users', { method: 'POST', body: payload })
      closeCreator()
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Falha ao criar acesso')
    } finally { setBusy(false) }
  }

  const uploadPhoto = async (user: DriverUser, file?: File) => {
    if (!file) return
    setUploadingId(user.id)
    setError('')
    try {
      const payload = new FormData()
      payload.set('photo', file)
      await api(`/api/users/${user.id}/photo`, { method: 'PUT', body: payload })
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Falha ao salvar a foto')
    } finally { setUploadingId('') }
  }

  const deactivate = async (user: DriverUser) => {
    try {
      await api(`/api/users/${user.id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !Number(user.isActive) }) })
      await load()
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Falha ao atualizar o acesso') }
  }

  const isDriver = form.role === 'DRIVER'
  return <div className="page">
    <header className="page-header"><div><p className="eyebrow">Acessos privados</p><h1>Equipe</h1><p>A foto identifica o motorista no mapa e é obrigatória antes de receber uma rota.</p></div><Button onClick={() => setOpen(true)}><Plus size={16} /> Adicionar pessoa</Button></header>
    {error && !open ? <div className="form-alert team-page-alert">{error}</div> : null}
    <section className="team-list">
      {users.map((user) => <article key={user.id}>
        <div className={`user-avatar team-user-photo ${user.role === 'DRIVER' && !user.hasPhoto ? 'photo-pending' : ''}`}>
          {user.photoUrl ? <img src={user.photoUrl} alt={`Foto de ${user.fullName}`} /> : <UserRound size={17} />}
        </div>
        <div><strong>{user.fullName}</strong><span>{user.email}{user.phone ? ` · ${user.phone}` : ''}</span></div>
        <span className="role-label">{user.role === 'DRIVER' ? 'Entregador' : user.role === 'MANAGER' ? 'Gestor' : 'Administrador'}</span>
        {user.role === 'DRIVER' && !user.hasPhoto ? <span className="team-photo-pending">Foto pendente</span> : <Status value={Number(user.isActive) ? 'ACTIVE' : 'INACTIVE'} />}
        <div className="team-row-actions">
          {user.role === 'DRIVER' ? <label className={`button button-secondary team-photo-button ${uploadingId === user.id ? 'is-loading' : ''}`}>
            <ImagePlus size={14} /> {uploadingId === user.id ? 'Enviando…' : user.hasPhoto ? 'Trocar foto' : 'Adicionar foto'}
            <input type="file" accept="image/jpeg,image/png,image/webp" capture="user" disabled={Boolean(uploadingId)} onChange={(event) => { void uploadPhoto(user, event.target.files?.[0]); event.currentTarget.value = '' }} />
          </label> : null}
          <Button variant="ghost" onClick={() => void deactivate(user)}>{Number(user.isActive) ? 'Desativar' : 'Ativar'}</Button>
        </div>
      </article>)}
    </section>

    {open ? <Modal title="Novo acesso" onClose={() => !busy && closeCreator()}>
      <div className="modal-body form-grid">
        <Field label="Nome completo" value={form.fullName} onChange={(event) => setForm({ ...form, fullName: event.target.value })} />
        <Field label="E-mail" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        <Field label="WhatsApp" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
        <SelectField label="Perfil" value={form.role} onChange={(event) => { setForm({ ...form, role: event.target.value }); if (event.target.value !== 'DRIVER') setPhoto(null) }}><option value="DRIVER">Entregador</option><option value="MANAGER">Gestor</option><option value="ADMIN">Administrador</option></SelectField>
        <SelectField label="Navegação preferida" value={form.preferredNavigation} onChange={(event) => setForm({ ...form, preferredNavigation: event.target.value })}><option value="google">Google Maps</option><option value="waze">Waze</option></SelectField>
        <Field label="Senha temporária" type="password" minLength={12} hint="Mínimo de 12 caracteres." value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
        {isDriver ? <label className={`driver-photo-picker full-span ${previewUrl ? 'has-preview' : ''}`}>
          {previewUrl ? <img src={previewUrl} alt="Prévia da foto do motorista" /> : <span><Camera size={22} /><strong>Foto obrigatória do motorista</strong><small>Use uma foto frontal, nítida e atual. JPG, PNG ou WebP até 5 MB.</small></span>}
          <input type="file" accept="image/jpeg,image/png,image/webp" capture="user" onChange={(event) => setPhoto(event.target.files?.[0] ?? null)} />
          {previewUrl ? <b>Trocar foto</b> : null}
        </label> : null}
        {error ? <div className="form-error full-span">{error}</div> : null}
      </div>
      <footer className="modal-footer"><Button variant="ghost" onClick={closeCreator}>Cancelar</Button><Button onClick={() => void create()} loading={busy} disabled={form.fullName.length < 2 || form.password.length < 12 || (isDriver && !photo)}>Criar acesso</Button></footer>
    </Modal> : null}
  </div>
}
